import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useQuery } from "react-query";
import JobStatus from "@/models/enums/job-status";
import JobDto from "@/models/interfaces/dtos/production/job-dto";
import { fetchItem } from "@/queries/queries-helper";
import ENDPOINTS from "@/services/endpoints";
import useProjectContext from "@/services/context/project/use-project-context";
import { ProjectProcessContext, ProjectProcessContextProps, ProjectProcessRunnerContext } from "./project-process-context-props";
import type { ProductionProcessOptions } from "./project-process-runner";

const REFRESH_INTERVAL = 5000;

const isJobRunning = (job: JobDto | null | undefined): job is JobDto =>
    job != null && (job.status === JobStatus.Pending || job.status === JobStatus.Processing);

const ProjectProcessProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const { project } = useProjectContext();
    const runner = useContext(ProjectProcessRunnerContext);
    if (!runner) throw new Error("ProjectProcessRunnerProvider est requis.");
    const running = useSyncExternalStore(runner.subscribe, runner.getSnapshot);
    const processProject = useMemo(() => ({
        projectId: project.id,
        projectBoomerId: project.projectBoomerId,
        baamerRevisionId: project.baamerRevisionId,
        newBoomerRevision: !project.boomerRevisionNumber,
    }), [project.id, project.projectBoomerId, project.baamerRevisionId, project.boomerRevisionNumber]);
    const runProductionProcess = useCallback((processName: string, jobUrl: string, options?: ProductionProcessOptions) =>
        runner.launch(processProject, processName, jobUrl, options), [runner, processProject]);
    const [activeProcesses, setActiveProcesses] = useState<Set<symbol>>(() => new Set());
    const activeProcessesRef = useRef(new Set<symbol>());
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    const beginProcess = useCallback(() => {
        const token = Symbol("project-process");
        activeProcessesRef.current.add(token);
        setActiveProcesses(new Set(activeProcessesRef.current));

        return () => {
            if (!activeProcessesRef.current.delete(token)) {
                return;
            }

            if (isMounted.current) {
                setActiveProcesses(new Set(activeProcessesRef.current));
            }
        };
    }, []);

    const { data, isLoading, error, refetch } = useQuery<JobDto | null, Error>(
        ["project-process", project.id],
        async () => await fetchItem<JobDto>(ENDPOINTS.PRODUCTION_GET_PREVIOUSLY_LAUNCHED(project.id)) ?? null,
        {
            // Après null, seule une nouvelle ouverture du projet ou une demande explicite relance la vérification.
            refetchInterval: job => isJobRunning(job) ? REFRESH_INTERVAL : false,
            refetchOnWindowFocus: query => isJobRunning(query.state.data),
            refetchOnReconnect: query => isJobRunning(query.state.data),
            cacheTime: 0,
            retry: false,
        }
    );

    const trackedJob = running.jobs.get(project.id);
    const observedJob = isJobRunning(data) ? data : null;
    const currentJob = isJobRunning(trackedJob) ? trackedJob : observedJob;
    const processError = running.errors.get(project.id) ?? error;
    const isTracked = running.activeProjects.has(project.id);
    const isProcessStatusKnown = data !== undefined && error === null;
    const observedProcess = useRef({ data, isProcessStatusKnown });

    useLayoutEffect(() => {
        observedProcess.current = { data, isProcessStatusKnown };
    }, [data, isProcessStatusKnown]);

    const isProcessBlockingEdits = useCallback(() =>
        !observedProcess.current.isProcessStatusKnown
        || isJobRunning(observedProcess.current.data)
        || activeProcessesRef.current.size > 0
        || runner.getSnapshot().activeProjects.has(project.id), [runner, project.id]);

    const refreshProcess = useCallback(async () => {
        await refetch({ throwOnError: true });
    }, [refetch]);

    const value = useMemo<ProjectProcessContextProps>(() => ({
        currentJob,
        // Les jobs locaux incluent aussi les détections, les envois et leurs callbacks.
        isProcessRunning: currentJob !== null || activeProcesses.size > 0 || isTracked,
        isLoading,
        isProcessStatusKnown,
        isProcessBlockingEdits,
        error: processError,
        refreshProcess,
        beginProcess,
        runProductionProcess,
    }), [currentJob, activeProcesses, isTracked, isLoading, isProcessStatusKnown, isProcessBlockingEdits, processError, refreshProcess, beginProcess, runProductionProcess]);

    return (
        <ProjectProcessContext.Provider value={value}>
            {children}
        </ProjectProcessContext.Provider>
    );
};

export default ProjectProcessProvider;
